/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Friday, 19th April 2019
 *
 *  Avatar Upload & Save Input Field
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { inject } from 'mobx-react';

// Material UI
import Input from '@material-ui/core/Input';
import CircularProgress from '@material-ui/core/CircularProgress';

// Images
import defaultAvatar from 'assets/images/avatar_placeholder.jpg';

// Globals
const uploadURL = process.env.REACT_APP_CLOUDINARY_UPLOAD_URL;
const uploadPresetAvatars = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET_AVATARS;
// File Size Limit in MB
const maxFileSize = 50;

class AvatarInput extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      file: null,
      response: null,
      imgUrl: '',
      isLoading: false,
    };
    this.handleClick = this.handleClick.bind(this);
    this.handleAvatarUpload = this.handleAvatarUpload.bind(this);
  }

  handleClick = e => {
    const avatarInput = document.getElementById('fileAvatar');
    avatarInput.click();
  };

  handleAvatarUpload(e) {
    // Get File from event
    const file = e.target.files[0];
    const fileSizeMB = e.target.files[0].size / 1024 / 1024;

    // Check for Max File Size Limit
    if (fileSizeMB > maxFileSize) {
      //Notify User
      this.props.store.portalStore.snackbar.showSnackbarWithDismiss(
        'Max file size of ' + maxFileSize + 'MB exceeded.',
        'error'
      );
      //Exit
      return;
    }

    // Exit if not a valid file.
    // This can happen when clicking cancel or esc when the dialog is open.
    if (file === undefined) {
      return;
    }

    this.setState({
      file: file,
      response: null,
      imgUrl: '',
      isLoading: true,
    });

    const url = uploadURL;
    const xhr = new XMLHttpRequest();
    const fd = new FormData();

    xhr.onerror = e => {
      // If Status = 0, There is an issue connecting to the API server, check the network.
      if (xhr.readyState === 4 && xhr.status === 0) {
        this.props.store.portalStore.snackbar.showSnackbarWithDismiss('Network Error - Please try again.', 'error');
        this.setState({
          isLoading: false,
        });
      }
    };

    xhr.open('POST', url, true);
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

    // HttpRequest State Change
    xhr.onreadystatechange = e => {
      // Post Completed Successfully
      if (xhr.readyState === 4 && xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);

        // Check for an image url
        if (response.secure_url !== '') {
          // Check for onChange Props Function, return state
          if (this.props.handleAvatarChange && typeof this.props.handleAvatarChange === 'function') {
            this.props.handleAvatarChange(file, response);
          }

          // Update state
          this.setState({
            imgUrl: response.secure_url,
            isLoading: false,
          });
        }

        // No Url, there could be an error
        this.setState({
          isLoading: false,
        });
      }

      // Check for Error/Bad Request
      if (xhr.readyState === 4 && xhr.status === 400) {
        const response = JSON.parse(xhr.response);
        this.props.store.portalStore.snackbar.showSnackbarWithDismiss(response.error.message, 'error');
        this.setState({
          isLoading: false,
        });
      }

      // If Status = 0, There is an issue connecting to the API server, check the network.
      if (xhr.readyState === 4 && xhr.status === 0) {
        this.props.store.portalStore.snackbar.showSnackbarWithDismiss('Network Error - Please try again.', 'error');
        this.setState({
          isLoading: false,
        });
      }
    };

    fd.append('upload_preset', uploadPresetAvatars);
    fd.append('tags', 'browser_upload');
    fd.append('tags', 'avatar_upload');
    fd.append('api_key', process.env.REACT_APP_CLOUDINARY_API_KEY);
    fd.append('file', file);
    xhr.send(fd);
  }

  render() {
    const { classes, url, handleAvatarChange, required, ...rest } = this.props;
    const { imgUrl, isLoading } = this.state;
    return (
      <div className={classes.wrapper}>
        <div className={classes.avatar}>
          <img
            id='avatarImg'
            src={url !== '' ? url : imgUrl === '' ? defaultAvatar : imgUrl}
            className={classes.avatarImg}
            alt='Avatar'
            onClick={this.handleClick}
          />
          <Input
            id='fileAvatar'
            type='file'
            onChange={this.handleAvatarUpload}
            className={classes.avatarInput}
            required={required && url === ''}
            {...rest}
          />
          {isLoading && <CircularProgress size={52} className={classes.avatarProgress} />}
        </div>
      </div>
    );
  }
}

AvatarInput.propTypes = {
  classes: PropTypes.object.isRequired,
  url: PropTypes.string,
  handleAvatarChange: PropTypes.func,
};

const styles = theme => ({
  wrapper: {
    position: 'relative',
    textAlign: 'center',
    width: '95px',
    height: 'auto',
    margin: '5px',
  },
  avatar: {
    cursor: 'pointer',
    width: '95px',
    height: '95px',
    borderRadius: '50%',
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
  },
  avatarInput: {
    width: '95px',
    cursor: 'pointer',
    display: 'block',
    left: '0',
    opacity: 0 + ' !important',
    position: 'absolute',
    top: 0,
  },
  avatarProgress: {
    zIndex: 1,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -25,
    marginLeft: -27,
  },
});

export default withStyles(styles, { withTheme: true })(inject('store')(AvatarInput));
